import {
  Box,
  List,
  Popover,
  PopoverOrigin,
  PopoverPosition,
  PopoverProps,
} from '@mui/material';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { KeyboardKeys } from '@shared/models/keyboard.model';
import { MenuContext } from './MenuContext';
import {
  MenuWidth,
  MenuContextValue,
  MenuFuzzySearchOptions,
  MENU_STYLE,
  SubmenuSide,
} from './menu.model';
import { useMenuRegistration } from '@ui/menus/Menu/hooks/use-menu-registration.hook';
import { useSubmenuSide } from '@ui/menus/Menu/hooks/use-submenu-side.hook';
import { EmptyStateItem } from './components/EmptyStateItem';
import { menuItemMatchesSearch } from './menu-search.helpers';
import { MenuSearch } from './MenuSearch';
import { MenuDivider } from './MenuDivider';
import { guards } from '@shared/helpers/typeguards.helpers';
import { is } from '@client/helpers/dom.guards';
import { useWindowEventListener } from '@client/hooks/use-events.hooks';
import { useHideHoverTooltips } from '@client/hooks/use-overlay-hover-tooltips.hook';
import { scrollSelectedMenuItemIntoCenter } from '@client/helpers/dom.helpers';

export type BaseMenuProps = {
  open: boolean;
  anchorEl?: Element | null;
  anchorPosition?: PopoverPosition;
  onClose?: () => void;
  width?: MenuWidth;
  /**
   * Paper min-width when {@link BaseMenuProps.width} is `auto`. Defaults to
   * {@link MENU_STYLE.MIN_WIDTH}; pass `0` for compact content-sized menus.
   */
  minWidth?: number;
  children: ReactNode;
  /** Type of menu. 'root' for the main menu, 'submenu' for submenus. The only difference is position correction for the popover. */
  type: 'root' | 'submenu';
  /** Fuzzy search options for filtering menu items */
  fuzzySearchOptions?: MenuFuzzySearchOptions;
  /** Optional callback to close parent menu(s). Used for nested menus. */
  onCloseAll?: () => void;
  /** Callback to close only the current menu (for Arrow Left/Escape). If not provided, uses onClose. */
  onCloseCurrent?: () => void;
  /** Point on the anchor element where the popover attaches */
  anchorOrigin?: PopoverOrigin;
  /** Point on the popover that aligns with the anchor origin */
  transformOrigin?: PopoverOrigin;
  /**
   * Side a `type='submenu'` popover should open towards relative to its anchor.
   * The menu flips to the opposite side when there is not enough room. Ignored
   * when explicit {@link BaseMenuProps.anchorOrigin} origins are provided.
   */
  preferredSide?: SubmenuSide;
  /** Prefer opening an anchored root menu upward, falling back downward when it does not fit. */
  preferredVerticalSide?: 'top' | 'bottom';
  /** Optional mouse leave handler on the Popover paper (useful for submenu hover interactions) */
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
  /** Optional mouse enter handler on the Popover paper (useful for submenu hover interactions) */
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  /** Text to display when no results are found */
  noResultsText?: string;
  /** When true, injects a search input as the first menu item. If string is provided, it will be used as the placeholder. */
  searchable?: boolean | string;
  /** Observe the shared search field, e.g. to offer creation of a new value. */
  onSearchChange?: (value: string) => void;
  /**
   * Skip returning focus to the trigger when the menu closes.
   * Needed when the click navigates away or opens an inline rename (the trigger may unmount).
   */
  disableRestoreFocus?: boolean;
  /**
   * Paper corner radius. Defaults to `2px`; pass e.g. `5px` to match
   * {@link PillTabs} on compact value pickers.
   */
  borderRadius?: string;
};

const SEARCH_HEADER_SX = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  bgcolor: 'background.paper',
  // Contain the divider's bottom margin so rows don't show through the gap.
  display: 'flow-root',
} as const;

/**
 * Base menu component. Should not be used directly, extend this with proxy components instead, or use Menu or Submenu instead which already extend this.
 * @param param0
 * @returns
 */
export const BaseMenu: React.FC<BaseMenuProps> = ({
  open,
  anchorEl,
  anchorPosition,
  onClose,
  onCloseAll,
  onCloseCurrent,
  width = 'auto',
  minWidth: minWidthProp,
  type = 'root',
  children,
  fuzzySearchOptions = { threshold: 0.2, minMatchCharLength: 1 },
  anchorOrigin: providedAnchorOrigin,
  transformOrigin: providedTransformOrigin,
  preferredSide = 'right',
  preferredVerticalSide = 'bottom',
  noResultsText = 'No results',
  searchable = false,
  onSearchChange,
  onMouseLeave,
  onMouseEnter,
  borderRadius = '2px',
  disableRestoreFocus = false,
}) => {
  const searchPlaceholder = guards.is.string(searchable)
    ? searchable
    : 'Search...';
  const { register: registerMenu } = useMenuRegistration();
  const [searchString, setSearchString] = useState('');
  useEffect(
    () => onSearchChange?.(searchString),
    [searchString, onSearchChange],
  );
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [hasVisibleItems, setHasVisibleItems] = useState(true);
  const [itemVisibility, setItemVisibility] = useState<Map<number, boolean>>(
    new Map(),
  );
  const itemRefsRef = useRef<Map<number, HTMLElement>>(new Map());
  const submenuCallbacksRef = useRef<
    Map<number, { open: () => void; close: () => void }>
  >(new Map());
  const [itemRefsVersion, setItemRefsVersion] = useState(0);
  const itemIndexCounterRef = useRef(0);
  const listRef = useRef<HTMLUListElement>(null);

  const registerItem = useCallback(
    (ref: HTMLElement | null, providedIndex?: number): number => {
      const index =
        providedIndex !== undefined
          ? providedIndex
          : itemIndexCounterRef.current++;
      if (ref) {
        itemRefsRef.current.set(index, ref);
      }
      setItemRefsVersion((v) => v + 1);
      return index;
    },
    [],
  );

  const unregisterItem = useCallback((index: number) => {
    itemRefsRef.current.delete(index);
    setItemVisibility((prev) => {
      if (!prev.has(index)) return prev;
      const next = new Map(prev);
      next.delete(index);
      return next;
    });
    setItemRefsVersion((v) => v + 1);
  }, []);

  const registerItemVisibility = useCallback(
    (itemIndex: number, isVisible: boolean) => {
      setItemVisibility((prev) => {
        if (prev.get(itemIndex) === isVisible) return prev;
        return new Map(prev).set(itemIndex, isVisible);
      });
    },
    [],
  );

  const registerChildIndex = useCallback((_index: number) => {
    // Root menu - no parent to bubble to, no-op (itemRefsVersion already incremented by registerItem)
  }, []);

  const unregisterChildIndex = useCallback((_index: number) => {
    // Root menu - no parent to bubble to, no-op (itemRefsVersion already incremented by unregisterItem)
  }, []);

  const registerSubmenu = useCallback(
    (itemIndex: number, openSubmenu: () => void, closeSubmenu: () => void) => {
      submenuCallbacksRef.current.set(itemIndex, {
        open: () => {
          // Close all other submenus before opening this one
          submenuCallbacksRef.current.forEach(({ close }, index) => {
            if (index !== itemIndex) close();
          });
          openSubmenu();
        },
        close: closeSubmenu,
      });
    },
    [],
  );

  const unregisterSubmenu = useCallback((itemIndex: number) => {
    submenuCallbacksRef.current.delete(itemIndex);
  }, []);

  const closeOtherSubmenus = useCallback((exceptIndex: number | null) => {
    submenuCallbacksRef.current.forEach(({ close }, index) => {
      if (index !== exceptIndex) {
        close();
      }
    });
  }, []);

  const closeAllMenus = useCallback(() => {
    onClose?.();
    onCloseAll?.();
  }, [onClose, onCloseAll]);

  const handleKeyDownRef = useRef<(e: KeyboardEvent) => void>(() => {});

  useEffect(() => {
    const getEnabledRows = () =>
      Array.from(
        listRef.current?.querySelectorAll<HTMLElement>(
          '[data-menu-index]:not([aria-disabled="true"])',
        ) ?? [],
      );

    const getActiveRow = () => getRowForElement(document.activeElement);

    const getRowForElement = (element: Element | null) => {
      if (!(element instanceof HTMLElement)) return null;
      return element.closest<HTMLElement>('[data-menu-index]');
    };

    const moveFocus = (direction: 'next' | 'prev') => {
      const rows = getEnabledRows();
      if (!rows.length) return;
      if (document.activeElement?.matches('[data-menu-search]')) {
        const currentIndex = rows.findIndex(
          (row) => Number(row.dataset.menuIndex) === focusedIndex,
        );
        const nextIndex = Math.max(
          -1,
          Math.min(
            rows.length - 1,
            currentIndex + (direction === 'next' ? 1 : -1),
          ),
        );
        const nextRow = rows[nextIndex];
        setFocusedIndex(nextRow ? Number(nextRow.dataset.menuIndex) : null);
        nextRow?.scrollIntoView?.({ block: 'nearest' });
        return;
      }
      const activeRow = getActiveRow();
      const startIndex = direction === 'next' ? -1 : 0; // for when no active row.
      const currentIndex = activeRow ? rows.indexOf(activeRow) : startIndex;
      const delta = direction === 'next' ? 1 : -1;
      const nextIndex = (currentIndex + delta + rows.length) % rows.length;
      rows[nextIndex]?.focus();
    };

    const focusEdge = (edge: 'first' | 'last') => {
      const rows = getEnabledRows();
      const target = edge === 'first' ? rows.at(0) : rows.at(-1);
      target?.focus();
    };

    handleKeyDownRef.current = (e: KeyboardEvent) => {
      if (!open || e.defaultPrevented || e.isComposing) return;
      // Only the frontmost menu handles keys; parent menus also listen on document.
      const frontmostMenu = Array.from(
        document.querySelectorAll('[data-type="menu"]'),
      ).at(-1);
      if (!frontmostMenu?.contains(listRef.current)) return;

      let action: (() => void) | null = null;

      switch (e.key) {
        case KeyboardKeys.ENTER:
          if (
            document.activeElement?.matches('[data-menu-search]') &&
            focusedIndex !== null
          ) {
            action = () => {
              const row = getEnabledRows().find(
                (item) => Number(item.dataset.menuIndex) === focusedIndex,
              );
              row?.click();
            };
          }
          break;
        case KeyboardKeys.ARROW_DOWN:
          action = () => moveFocus('next');
          break;
        case KeyboardKeys.ARROW_UP:
          action = () => moveFocus('prev');
          break;
        case KeyboardKeys.HOME:
          if (document.activeElement?.matches('[data-menu-search]')) break;
          action = () => focusEdge('first');
          break;
        case KeyboardKeys.END:
          if (document.activeElement?.matches('[data-menu-search]')) break;
          action = () => focusEdge('last');
          break;
        case KeyboardKeys.ARROW_RIGHT:
          if (document.activeElement?.matches('[data-menu-search]')) break;
          action = () => {
            const targetIndex = getActiveRow()?.getAttribute('data-menu-index');
            if (guards.is.defined(targetIndex)) {
              submenuCallbacksRef.current
                .get(parseInt(targetIndex, 10))
                ?.open();
            }
          };
          break;
        case KeyboardKeys.ARROW_LEFT:
          if (document.activeElement?.matches('[data-menu-search]')) break;
          action = () => (onCloseCurrent ?? onClose)?.();
          break;
        case KeyboardKeys.ESCAPE:
          action = () => (onCloseCurrent ?? onClose)?.();
          break;
      }

      if (action) {
        e.preventDefault();
        e.stopPropagation();
        action();
      }
    };
  }, [open, onClose, onCloseCurrent, focusedIndex]);

  // Offer the sole search result without taking focus away from typing.
  useEffect(() => {
    if (!open || !searchable) return;
    const input = listRef.current?.querySelector('[data-menu-search]');
    if (document.activeElement !== input) return;
    const rows = Array.from(itemRefsRef.current.entries()).filter(
      ([index, element]) =>
        itemVisibility.get(index) !== false &&
        element.isConnected &&
        element.getAttribute('aria-disabled') !== 'true',
    );
    setFocusedIndex(
      searchString.trim() && rows.length === 1 ? (rows[0]?.[0] ?? null) : null,
    );
  }, [open, searchable, searchString, itemVisibility, itemRefsVersion]);

  // Check if there are visible items when search is active
  useEffect(() => {
    if (!searchString) {
      setHasVisibleItems(true);
      return;
    }

    // Check if any registered items are visible using the visibility map
    const hasVisible = Array.from(itemRefsRef.current.keys()).some((index) => {
      // Default to true if not registered (item hasn't computed visibility yet)
      return itemVisibility.get(index) !== false;
    });

    setHasVisibleItems(hasVisible);
  }, [searchString, itemVisibility, itemRefsVersion]);

  useEffect(() => {
    if (!open) {
      setSearchString('');
      setFocusedIndex(null);
      setHasVisibleItems(true);
      itemRefsRef.current.clear();
      setItemVisibility(new Map());
      itemIndexCounterRef.current = 0;
      setItemRefsVersion((v) => v + 1);
    } else {
      const handler = (e: KeyboardEvent) => handleKeyDownRef.current?.(e);
      document.addEventListener('keydown', handler);
      return () => document.removeEventListener('keydown', handler);
    }
  }, [open]);

  useEffect(() => {
    if (!open || type !== 'root') return;
    return registerMenu(onClose ?? (() => {}));
  }, [open, type, onClose, registerMenu]);

  useHideHoverTooltips(open);

  const [searchHeaderHeight, setSearchHeaderHeight] = useState(0);
  const searchHeaderRef = useCallback((node: HTMLDivElement | null) => {
    setSearchHeaderHeight(node?.offsetHeight ?? 0);
  }, []);

  const [paperElement, setPaperElement] = useState<HTMLDivElement | null>(null);
  const [anchorWidth, setAnchorWidth] = useState<number | undefined>(undefined);
  const paperRefCallback = useCallback((node: HTMLDivElement | null) => {
    setPaperElement(node);
    if (!node) return;
    // Flush layout so MUI's max-height is applied before we test overflow.
    void node.offsetHeight;
    scrollSelectedMenuItemIntoCenter(node);
  }, []);

  useLayoutEffect(() => {
    if (!open || !paperElement) return;
    scrollSelectedMenuItemIntoCenter(paperElement);
  }, [open, paperElement]);

  const handlePointerDown = useCallback(
    (event: Event) => {
      if (!open || type !== 'root') return;
      if (!is.Element(event.target)) return;
      if (event.target.closest('[data-type="menu"]')) {
        // is inside a menu, do nothing
        return;
      }
      if (paperElement?.contains(event.target)) return;
      // The trigger is handled on click instead, see handleTriggerClick.
      if (anchorEl?.contains(event.target)) return;
      onClose?.();
    },
    [open, type, paperElement, anchorEl, onClose],
  );
  useWindowEventListener('pointerdown', handlePointerDown, true);

  // Root menus consume ordinary backdrop clicks, but right-click should open
  // the context menu of the element underneath in the same gesture.
  const handleOutsideContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!open || !is.Element(event.target)) return;
      if (paperElement?.contains(event.target)) {
        // A row is a menu control; the browser menu would cover it.
        // Stop the bubble so a submenu row is not also an outside click on the root.
        if (event.target.closest('[data-menu-index]')) event.preventDefault();
        event.stopPropagation();
        return;
      }
      // Only a root menu forwards a right-click to the element underneath.
      if (type !== 'root') return;
      const target = document
        .elementsFromPoint(event.clientX, event.clientY)
        .find((element) => !element.closest('[data-type="menu"]'));
      event.preventDefault();
      event.stopPropagation();
      closeAllMenus();
      target?.dispatchEvent(
        new MouseEvent('contextmenu', {
          bubbles: true,
          cancelable: true,
          clientX: event.clientX,
          clientY: event.clientY,
          screenX: event.screenX,
          screenY: event.screenY,
          button: 2,
          buttons: event.buttons,
          altKey: event.altKey,
          ctrlKey: event.ctrlKey,
          metaKey: event.metaKey,
          shiftKey: event.shiftKey,
        }),
      );
    },
    [open, type, paperElement, closeAllMenus],
  );

  // Also guard clicks dispatched directly to the trigger (e.g. keyboard or
  // programmatic activation), so its own onClick cannot reopen the menu.
  // Capturing on window runs ahead of React's listener on the root container.
  const handleTriggerClick = useCallback(
    (event: Event) => {
      if (!open || type !== 'root') return;
      if (!is.Element(event.target)) return;
      if (!anchorEl?.contains(event.target)) return;
      event.stopPropagation();
      onClose?.();
    },
    [open, type, anchorEl, onClose],
  );
  useWindowEventListener('click', handleTriggerClick, true);

  useEffect(() => {
    if (!anchorEl) {
      setAnchorWidth(undefined);
      return;
    }

    const nextWidth = anchorEl.getBoundingClientRect().width || undefined;
    setAnchorWidth(nextWidth);

    const resizeObserver = new ResizeObserver((entries) => {
      const width = entries.at(0)?.contentRect.width ?? 0;
      setAnchorWidth(width || undefined);
    });

    resizeObserver.observe(anchorEl);
    return () => resizeObserver.disconnect();
  }, [anchorEl]);

  const resolvedWidth = useMemo<MenuWidth | number>(() => {
    if (width === 'anchorEl') {
      return anchorWidth ?? MENU_STYLE.MIN_WIDTH;
    }
    return width;
  }, [anchorWidth, width]);

  const lastWidestWidth = useLastWidestWidth(paperElement, {
    initial: guards.is.number(resolvedWidth)
      ? resolvedWidth
      : MENU_STYLE.MIN_WIDTH,
  });

  const focusSearchInput = useCallback(() => {
    // Keep the selected row centered; default focus() would scroll the
    // search field at the top of the paper into view.
    listRef.current
      ?.querySelector<HTMLInputElement>('[data-menu-search]')
      ?.focus({ preventScroll: true });
  }, []);

  const popoverSlotProps = useMemo(() => {
    const paperMargins =
      type === 'submenu'
        ? { mt: `calc(-${MENU_STYLE.PADDING_Y * 4}px - 1px)` } // -1px to account for the border
        : {};
    const paperWidth = width === 'auto' ? 'max-content' : resolvedWidth;
    const paperMinWidth =
      width === 'auto'
        ? (minWidthProp ?? MENU_STYLE.MIN_WIDTH)
        : lastWidestWidth;
    return {
      paper: {
        ref: paperRefCallback,
        sx: {
          display: 'flex',
          flexDirection: 'column',
          width: paperWidth,
          minWidth: paperMinWidth,
          maxWidth: MENU_STYLE.MAX_WIDTH,
          // Keeps keyboard-focused rows from scrolling under the sticky search.
          scrollPaddingTop: `${searchHeaderHeight}px`,
          ...paperMargins,
          outline: 0,
          typography: 'body2',
          pointerEvents: 'all',
          border: `1px solid rgba(0, 0, 0, 0.3)`, // not sure which theme color to use here
          borderRadius,
        },
        onMouseLeave: onMouseLeave,
        onMouseEnter: onMouseEnter,
      },
      root: { sx: { pointerEvents: type === 'root' ? 'auto' : 'none' } },
      transition: searchable
        ? {
            onEntered: focusSearchInput,
          }
        : undefined,
    } satisfies PopoverProps['slotProps'];
  }, [
    type,
    onMouseLeave,
    onMouseEnter,
    lastWidestWidth,
    resolvedWidth,
    paperRefCallback,
    width,
    minWidthProp,
    searchable,
    focusSearchInput,
    borderRadius,
    searchHeaderHeight,
  ]);

  const {
    resolvedSide,
    anchorOrigin: submenuAnchorOrigin,
    transformOrigin: submenuTransformOrigin,
  } = useSubmenuSide({
    type,
    open,
    anchorEl,
    paperElement,
    preferredSide,
    anchorWidth,
  });

  const [alignRight, setAlignRight] = useState(false);
  useLayoutEffect(() => {
    if (
      type !== 'root' ||
      !open ||
      !anchorEl ||
      !paperElement ||
      providedAnchorOrigin ||
      providedTransformOrigin
    ) {
      setAlignRight(false);
      return;
    }
    const update = () => {
      const anchor = anchorEl.getBoundingClientRect();
      const menuWidth = paperElement.getBoundingClientRect().width;
      setAlignRight(anchor.left + menuWidth > window.innerWidth);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(paperElement);
    observer.observe(anchorEl);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [
    type,
    open,
    anchorEl,
    paperElement,
    providedAnchorOrigin,
    providedTransformOrigin,
  ]);
  const rootHorizontal = alignRight ? 'right' : 'left';
  const [opensUp, setOpensUp] = useState(false);
  useLayoutEffect(() => {
    if (
      type !== 'root' ||
      preferredVerticalSide !== 'top' ||
      !open ||
      !anchorEl ||
      !paperElement
    ) {
      setOpensUp(false);
      return;
    }
    const update = () =>
      setOpensUp(
        anchorEl.getBoundingClientRect().top >= paperElement.scrollHeight,
      );
    update();
    const observer = new ResizeObserver(update);
    observer.observe(paperElement);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [type, preferredVerticalSide, open, anchorEl, paperElement]);

  const anchorOrigin =
    providedAnchorOrigin ??
    (type === 'submenu'
      ? submenuAnchorOrigin
      : opensUp
        ? { vertical: 'top', horizontal: rootHorizontal }
        : { vertical: 'bottom', horizontal: rootHorizontal });
  const transformOrigin =
    providedTransformOrigin ??
    (type === 'submenu'
      ? submenuTransformOrigin
      : opensUp
        ? { vertical: 'bottom', horizontal: rootHorizontal }
        : { vertical: 'top', horizontal: rootHorizontal });

  const matchesSearch = useCallback(
    (text: string): boolean =>
      menuItemMatchesSearch(text, searchString, fuzzySearchOptions),
    [searchString, fuzzySearchOptions],
  );

  const contextValue = useMemo<MenuContextValue>(
    () => ({
      isOpen: open,
      closeMenu: onClose ?? (() => {}),
      closeAllMenus,
      searchString,
      setSearchString,
      matchesSearch,
      focusedIndex,
      setFocusedIndex,
      registerItem,
      unregisterItem,
      registerItemVisibility,
      registerChildIndex,
      unregisterChildIndex,
      itemVisibility,
      registerSubmenu,
      unregisterSubmenu,
      closeOtherSubmenus,
      itemRefs: itemRefsRef.current,
      fuzzySearchOptions,
      width,
      submenuSide: resolvedSide,
    }),
    [
      open,
      onClose,
      closeAllMenus,
      searchString,
      matchesSearch,
      focusedIndex,
      registerItem,
      unregisterItem,
      registerItemVisibility,
      registerChildIndex,
      unregisterChildIndex,
      registerSubmenu,
      unregisterSubmenu,
      closeOtherSubmenus,
      itemVisibility,
      fuzzySearchOptions,
      width,
      resolvedSide,
    ],
  );
  const sx = useMemo(
    () => ({
      py: MENU_STYLE.PADDING_Y,
      // Paper is a flex column; keep the list from shrinking so the paper
      // stays the scroll parent without changing its height.
      flexShrink: 0,
    }),
    [],
  );

  return (
    <Popover
      open={open}
      disableAutoFocus={Boolean(searchable)}
      disableEnforceFocus={type === 'submenu'}
      disableRestoreFocus={type === 'submenu' || disableRestoreFocus}
      anchorEl={anchorEl}
      anchorPosition={anchorPosition}
      anchorReference={anchorEl ? 'anchorEl' : 'anchorPosition'}
      marginThreshold={0}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      data-type="menu"
      slotProps={popoverSlotProps}
      hideBackdrop={type === 'submenu'}
      onContextMenu={handleOutsideContextMenu}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      onClose={onClose}
    >
      <MenuContext.Provider value={contextValue}>
        <List
          ref={listRef}
          disablePadding
          sx={sx}
          onFocus={(event) => {
            // React focus events bubble through portals into ancestor menus.
            // Only rows physically inside this list may change its highlight.
            if (
              !(event.target instanceof HTMLElement) ||
              !event.currentTarget.contains(event.target)
            )
              return;
            const row = event.target.closest<HTMLElement>('[data-menu-index]');
            setFocusedIndex(row ? Number(row.dataset.menuIndex) : null);
          }}
        >
          {searchable && (
            <Box ref={searchHeaderRef} sx={SEARCH_HEADER_SX}>
              <MenuSearch placeholder={searchPlaceholder} />
              <MenuDivider />
            </Box>
          )}
          {children}
          {searchString && !hasVisibleItems && (
            <EmptyStateItem>{noResultsText}</EmptyStateItem>
          )}
        </List>
      </MenuContext.Provider>
    </Popover>
  );
};

const useLastWidestWidth = (
  element: HTMLElement | null,
  { initial }: { initial: number },
) => {
  const [lastWidestWidth, setLastWidestWidth] = useState<number>(initial);

  useEffect(() => {
    if (!element) {
      setLastWidestWidth(initial);
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const width = entries.at(0)?.contentRect.width ?? 0;
      setLastWidestWidth((prev) => Math.max(prev, width));
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [element, initial]);

  return lastWidestWidth;
};
